<script setup>
// NOOVI: resumo de canais na Overview — primeiro consumidor frontend de
// summary_reports#channel (V2::Reports::ChannelSummaryBuilder). A resposta é
// um objeto chaveado por channel_type ("Channel::WebWidget", "Channel::Api"…)
// — ver a nota em store/modules/summaryReports.js sobre por que essa chave
// não pode passar por camelcaseKeys.
import { computed, inject, onMounted, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { DonutChart } from '@chatwoot/viz';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import ChannelIcon from 'dashboard/components-next/icon/ChannelIcon.vue';
import { CHANNEL_TYPE_I18N_KEY_MAP } from 'dashboard/helper/inbox';
import OverviewCard from './overview/MetricCard.vue';
import {
  OVERVIEW_DATE_FILTER_KEY,
  createDefaultDateFilter,
} from '../helpers/overviewDateFilter';

const { t } = useI18n();
const store = useStore();
const dateFilter = inject(
  OVERVIEW_DATE_FILTER_KEY,
  createDefaultDateFilter,
  true
);

const channelReports = useMapGetter('summaryReports/getChannelSummaryReports');
const uiFlags = useMapGetter('summaryReports/getUIFlags');

const isLoading = computed(() => uiFlags.value.isFetchingChannelSummaryReports);

const channelLabel = channelType => {
  const i18nKey = CHANNEL_TYPE_I18N_KEY_MAP[channelType];
  return i18nKey ? t(`INBOX_MGMT.CHANNELS.${i18nKey}`) : channelType;
};

const chartData = computed(() => ({
  segments: Object.entries(channelReports.value)
    .map(([channelType, stats]) => ({
      id: channelType,
      label: channelLabel(channelType),
      value: stats.total,
    }))
    .filter(segment => segment.value > 0),
}));

const totalConversations = computed(() =>
  chartData.value.segments.reduce((sum, segment) => sum + segment.value, 0)
);

const fetchChannelSummary = () => {
  store.dispatch('summaryReports/fetchChannelSummaryReports', {
    since: dateFilter.from,
    until: dateFilter.to,
    businessHours: dateFilter.businessHours,
  });
};

onMounted(fetchChannelSummary);
watch(
  () => [dateFilter.from, dateFilter.to, dateFilter.businessHours],
  fetchChannelSummary
);
</script>

<template>
  <OverviewCard
    :is-loading="isLoading"
    :loading-message="t('OVERVIEW_REPORTS.CHANNEL_SUMMARY.LOADING_MESSAGE')"
  >
    <template #header>
      <div class="flex items-center gap-2 flex-row flex-wrap min-w-0">
        <h5 class="mb-0 text-n-slate-12 font-medium text-lg break-words">
          {{ t('OVERVIEW_REPORTS.CHANNEL_SUMMARY.HEADER') }}
        </h5>
      </div>
    </template>
    <DonutChart
      v-if="totalConversations"
      :data="chartData"
      :aria-label="t('OVERVIEW_REPORTS.CHANNEL_SUMMARY.HEADER')"
    >
      <template
        #legend-item="{ item, label, formattedValue, formattedPercentage }"
      >
        <ChannelIcon
          :inbox="{ channel_type: item.id }"
          class="size-3.5 text-n-slate-11"
        />
        <span class="text-sm text-n-slate-11">{{ label }}</span>
        <span class="text-sm font-medium text-n-slate-12">
          {{ formattedValue }}
        </span>
        <span class="text-xs text-n-slate-10">({{ formattedPercentage }})</span>
      </template>
    </DonutChart>
    <p v-else class="text-sm text-n-slate-10">
      {{ t('OVERVIEW_REPORTS.CHANNEL_SUMMARY.NO_CONVERSATIONS') }}
    </p>
  </OverviewCard>
</template>
